/**
 * WebWorker Bridge for Voxel CSG Engine
 * Provides parallel voxel processing using Web Workers
 *
 * Features:
 * - Multi-threaded voxel removal
 * - Worker pool management
 * - Automatic work distribution
 * - Progress tracking
 */

import { VoxelBBox, VoxelToolGeometry } from './VoxelCSGEngine';

// ── Type Definitions ────────────────────────────────────────────────────────

export interface VoxelWorkerMessage {
  type: 'init' | 'remove' | 'mesh' | 'stats' | 'reset';
  data?: any;
  workerId?: number;
}

export interface VoxelWorkerResponse {
  type: 'ready' | 'complete' | 'progress' | 'error';
  data?: any;
  workerId?: number;
  executionTime?: number;
}

export interface VoxelProcessResult {
  modifiedVoxels: number;
  executionTime: number;
  success: boolean;
  meshData?: {
    vertices: Float32Array;
    normals: Float32Array;
    indices: Uint32Array;
  };
}

export interface WorkerPoolConfig {
  numWorkers: number;
  workerScript: string;
}

// ── Worker Pool Manager ─────────────────────────────────────────────────────

class VoxelWorkerPool {
  private workers: Worker[] = [];
  private availableWorkers: Worker[] = [];
  private pendingTasks: Array<{
    message: VoxelWorkerMessage;
    resolve: (result: VoxelWorkerResponse) => void;
    reject: (error: any) => void;
  }> = [];

  private initialized = false;
  private workerCount = 0;

  constructor(private config: WorkerPoolConfig) {
    this.workerCount = config.numWorkers;
  }

  /**
   * Initialize worker pool
   */
  async initialize(): Promise<boolean> {
    // Initializing workers

    try {
      // Create workers
      for (let i = 0; i < this.workerCount; i++) {
        const worker = this.createWorker(i);
        this.workers.push(worker);
        this.availableWorkers.push(worker);
      }

      this.initialized = true;
      // Workers initialized
      return true;

    } catch (error) {
      console.error('[VoxelWorkerPool] Failed to initialize:', error);
      return false;
    }
  }

  /**
   * Create a single worker
   */
  private createWorker(workerId: number): Worker {
    // Create inline worker (since we can't use separate files easily)
    const workerCode = `
      // Voxel Worker - runs in separate thread
      let voxelData = null;
      let gridConfig = null;

      self.onmessage = function(e) {
        const { type, data, workerId } = e.data;

        try {
          switch (type) {
            case 'init':
              gridConfig = data.gridConfig;
              voxelData = new Uint32Array(data.voxelDataSize);
              voxelData.fill(1); // Initialize as full
              self.postMessage({
                type: 'ready',
                workerId,
                data: { success: true }
              });
              break;

            case 'remove':
              const result = removeVoxels(data);
              self.postMessage({
                type: 'complete',
                workerId,
                data: result
              });
              break;

            case 'mesh':
              const meshData = generateMesh(data);
              self.postMessage({
                type: 'complete',
                workerId,
                data: meshData
              }, [meshData.vertices.buffer, meshData.normals.buffer, meshData.indices.buffer]);
              break;

            case 'stats':
              const stats = calculateStats();
              self.postMessage({
                type: 'complete',
                workerId,
                data: stats
              });
              break;

            case 'reset':
              if (voxelData) {
                voxelData.fill(1);
              }
              self.postMessage({
                type: 'complete',
                workerId,
                data: { success: true }
              });
              break;

            default:
              throw new Error('Unknown message type: ' + type);
          }
        } catch (error) {
          self.postMessage({
            type: 'error',
            workerId,
            data: { error: error.message }
          });
        }
      };

      // Remove voxels within tool bounds
      function removeVoxels(params) {
        const { centerX, centerY, centerZ, tool, bounds } = params;
        const { minX, maxX, minY, maxY, minZ, maxZ } = bounds;

        let modifiedCount = 0;
        const startTime = performance.now();

        const { resX, resY, resZ, voxelSize, origin } = gridConfig;
        const radius = tool.diameter / 2;

        // Calculate voxel bounds
        const iMin = Math.max(0, Math.floor((minX - origin[0]) / voxelSize));
        const iMax = Math.min(resX - 1, Math.ceil((maxX - origin[0]) / voxelSize));
        const jMin = Math.max(0, Math.floor((minY - origin[1]) / voxelSize));
        const jMax = Math.min(resY - 1, Math.ceil((maxY - origin[1]) / voxelSize));
        const kMin = Math.max(0, Math.floor((minZ - origin[2]) / voxelSize));
        const kMax = Math.min(resZ - 1, Math.ceil((maxZ - origin[2]) / voxelSize));

        // Iterate through voxels
        for (let k = kMin; k <= kMax; k++) {
          for (let j = jMin; j <= jMax; j++) {
            for (let i = iMin; i <= iMax; i++) {
              const index = i + j * resX + k * resX * resY;

              // Skip if already empty
              if (voxelData[index] === 0) continue;

              // Calculate voxel center
              const vx = origin[0] + (i + 0.5) * voxelSize;
              const vy = origin[1] + (j + 0.5) * voxelSize;
              const vz = origin[2] + (k + 0.5) * voxelSize;

              // Check if inside tool
              if (isInsideTool(vx, vy, vz, centerX, centerY, centerZ, tool)) {
                voxelData[index] = 0;
                modifiedCount++;
              }
            }
          }
        }

        return {
          modifiedVoxels: modifiedCount,
          executionTime: performance.now() - startTime,
          success: true
        };
      }

      // Check if point is inside tool
      function isInsideTool(x, y, z, cx, cy, cz, tool) {
        const dx = x - cx;
        const dy = y - cy;
        const dz = z - cz;
        const radius = tool.diameter / 2;

        switch (tool.type) {
          case 'flat': {
            const distXY = Math.sqrt(dx*dx + dy*dy);
            return distXY <= radius && dz >= -tool.length && dz <= 0;
          }
          case 'ball': {
            const dist = Math.sqrt(dx*dx + dy*dy + dz*dz);
            return dist <= radius;
          }
          case 'bull': {
            const distXY = Math.sqrt(dx*dx + dy*dy);
            if (distXY <= radius - tool.cornerRadius) {
              return dz >= -tool.length && dz <= 0;
            }
            const cornerDist = Math.sqrt(
              Math.pow(distXY - (radius - tool.cornerRadius), 2) +
              Math.pow(Math.min(0, dz), 2)
            );
            return cornerDist <= tool.cornerRadius;
          }
          default:
            return false;
        }
      }

      // Generate mesh (simplified)
      function generateMesh(params) {
        const { resX, resY, resZ, voxelSize, origin } = gridConfig;
        const vertices = [];
        const normals = [];
        const indices = [];

        // Simple box representation for each non-empty voxel
        for (let k = 0; k < resZ; k++) {
          for (let j = 0; j < resY; j++) {
            for (let i = 0; i < resX; i++) {
              const index = i + j * resX + k * resX * resY;
              if (voxelData[index] === 0) continue;

              const x0 = origin[0] + i * voxelSize;
              const y0 = origin[1] + j * voxelSize;
              const z0 = origin[2] + k * voxelSize;
              const x1 = x0 + voxelSize;
              const y1 = y0 + voxelSize;
              const z1 = z0 + voxelSize;

              // Add box faces (simplified - just top face for now)
              const baseIdx = vertices.length / 3;

              vertices.push(
                x0, y1, z0,  x1, y1, z0,  x1, y1, z1,
                x0, y1, z0,  x1, y1, z1,  x0, y1, z1
              );

              for (let n = 0; n < 6; n++) {
                normals.push(0, 1, 0);
              }

              for (let n = 0; n < 6; n++) {
                indices.push(baseIdx + n);
              }
            }
          }
        }

        return {
          vertices: new Float32Array(vertices),
          normals: new Float32Array(normals),
          indices: new Uint32Array(indices)
        };
      }

      // Calculate statistics
      function calculateStats() {
        let activeVoxels = 0;
        for (let i = 0; i < voxelData.length; i++) {
          if (voxelData[i] > 0) activeVoxels++;
        }

        return {
          totalVoxels: voxelData.length,
          activeVoxels,
          memoryUsage: voxelData.byteLength
        };
      }
    `;

    const blob = new Blob([workerCode], { type: 'application/javascript' });
    const workerUrl = URL.createObjectURL(blob);
    const worker = new Worker(workerUrl);

    // Store worker ID
    (worker as any).workerId = workerId;

    // Setup message handler
    worker.onmessage = (e: MessageEvent<VoxelWorkerResponse>) => {
      this.handleWorkerMessage(worker, e.data);
    };

    worker.onerror = (error) => {
      console.error(`[VoxelWorkerPool] Worker ${workerId} error:`, error);
    };

    return worker;
  }

  /**
   * Handle message from worker
   */
  private handleWorkerMessage(worker: Worker, response: VoxelWorkerResponse): void {
    // Find pending task for this worker
    const taskIndex = this.pendingTasks.findIndex(
      task => task.message.workerId === response.workerId
    );

    if (taskIndex !== -1) {
      const task = this.pendingTasks[taskIndex];
      this.pendingTasks.splice(taskIndex, 1);

      // Resolve promise
      if (response.type === 'error') {
        task.reject(response.data);
      } else {
        task.resolve(response);
      }

      // Mark worker as available
      this.availableWorkers.push(worker);

      // Process next task if any
      this.processNextTask();
    }
  }

  /**
   * Execute task on worker
   */
  async executeTask(message: VoxelWorkerMessage): Promise<VoxelWorkerResponse> {
    return new Promise((resolve, reject) => {
      // Add to pending tasks
      this.pendingTasks.push({ message, resolve, reject });

      // Try to process immediately
      this.processNextTask();
    });
  }

  /**
   * Process next pending task
   */
  private processNextTask(): void {
    if (this.pendingTasks.length === 0 || this.availableWorkers.length === 0) {
      return;
    }

    const worker = this.availableWorkers.shift()!;
    const task = this.pendingTasks[0]; // Keep in queue until resolved

    // Assign worker ID to message
    task.message.workerId = (worker as any).workerId;

    // Post message to worker
    worker.postMessage(task.message);
  }

  /**
   * Terminate all workers
   */
  dispose(): void {
    for (const worker of this.workers) {
      worker.terminate();
    }

    this.workers = [];
    this.availableWorkers = [];
    this.pendingTasks = [];
    this.initialized = false;

    // VoxelWorkerPool disposed
  }

  isInitialized(): boolean {
    return this.initialized;
  }
}

// ── Main Worker Bridge ──────────────────────────────────────────────────────

export class VoxelWorkerBridge {
  private pool: VoxelWorkerPool;
  private initialized = false;

  constructor(numWorkers: number = navigator.hardwareConcurrency || 4) {
    this.pool = new VoxelWorkerPool({
      numWorkers: Math.max(1, Math.min(numWorkers, 8)), // Limit to 1-8 workers
      workerScript: '', // Not used (inline worker)
    });
  }

  /**
   * Initialize worker bridge
   */
  async initialize(
    resX: number,
    resY: number,
    resZ: number,
    voxelSize: number,
    bbox: VoxelBBox
  ): Promise<boolean> {
    // VoxelWorkerBridge initializing

    // Initialize worker pool
    const poolInitialized = await this.pool.initialize();
    if (!poolInitialized) {
      return false;
    }

    // Send init message to all workers
    const initMessage: VoxelWorkerMessage = {
      type: 'init',
      data: {
        gridConfig: {
          resX, resY, resZ,
          voxelSize,
          origin: [bbox.minX, bbox.minY, bbox.minZ],
        },
        voxelDataSize: resX * resY * resZ,
      },
    };

    try {
      await this.pool.executeTask(initMessage);
      this.initialized = true;
      // VoxelWorkerBridge initialized
      return true;
    } catch (error) {
      console.error('[VoxelWorkerBridge] Failed to initialize:', error);
      return false;
    }
  }

  /**
   * Remove voxels using worker
   */
  async removeVoxels(
    centerX: number,
    centerY: number,
    centerZ: number,
    tool: VoxelToolGeometry
  ): Promise<VoxelProcessResult> {
    if (!this.initialized) {
      return { modifiedVoxels: 0, executionTime: 0, success: false };
    }

    const radius = tool.diameter / 2;
    const bounds = {
      minX: centerX - radius,
      maxX: centerX + radius,
      minY: centerY - radius,
      maxY: centerY + radius,
      minZ: centerZ - tool.length,
      maxZ: centerZ + radius,
    };

    const message: VoxelWorkerMessage = {
      type: 'remove',
      data: { centerX, centerY, centerZ, tool, bounds },
    };

    try {
      const response = await this.pool.executeTask(message);
      return response.data as VoxelProcessResult;
    } catch (error) {
      console.error('[VoxelWorkerBridge] Remove operation failed:', error);
      return { modifiedVoxels: 0, executionTime: 0, success: false };
    }
  }

  /**
   * Generate mesh using worker
   */
  async generateMesh(): Promise<VoxelProcessResult> {
    if (!this.initialized) {
      return { modifiedVoxels: 0, executionTime: 0, success: false };
    }

    const message: VoxelWorkerMessage = {
      type: 'mesh',
      data: {},
    };

    try {
      const response = await this.pool.executeTask(message);
      return {
        modifiedVoxels: 0,
        executionTime: response.executionTime || 0,
        success: true,
        meshData: response.data,
      };
    } catch (error) {
      console.error('[VoxelWorkerBridge] Mesh generation failed:', error);
      return { modifiedVoxels: 0, executionTime: 0, success: false };
    }
  }

  /**
   * Get statistics from worker
   */
  async getStats(): Promise<any> {
    if (!this.initialized) {
      return null;
    }

    const message: VoxelWorkerMessage = {
      type: 'stats',
      data: {},
    };

    try {
      const response = await this.pool.executeTask(message);
      return response.data;
    } catch (error) {
      console.error('[VoxelWorkerBridge] Stats operation failed:', error);
      return null;
    }
  }

  /**
   * Reset voxel data
   */
  async reset(): Promise<boolean> {
    if (!this.initialized) {
      return false;
    }

    const message: VoxelWorkerMessage = {
      type: 'reset',
      data: {},
    };

    try {
      const response = await this.pool.executeTask(message);
      return response.data?.success || false;
    } catch (error) {
      console.error('[VoxelWorkerBridge] Reset operation failed:', error);
      return false;
    }
  }

  /**
   * Cleanup resources
   */
  dispose(): void {
    this.pool.dispose();
    this.initialized = false;
    // VoxelWorkerBridge disposed
  }

  isInitialized(): boolean {
    return this.initialized;
  }
}

// ── Factory Function ────────────────────────────────────────────────────────

export function createVoxelWorkerBridge(
  numWorkers?: number
): VoxelWorkerBridge {
  return new VoxelWorkerBridge(numWorkers);
}
